import { create } from 'zustand';
import type { Product, ProductFilters } from '@/types/inventory.types';

interface ProductState {
  // State
  products: Product[];
  isLoading: boolean;
  filters: ProductFilters;
  selectedProduct: Product | null;

  // Actions - Products
  setProducts: (products: Product[]) => void;
  addProduct: (product: Product) => void;
  updateProduct: (productId: string, updates: Partial<Product>) => void;
  removeProduct: (productId: string) => void;
  clearProducts: () => void;

  // Actions - Selection
  setSelectedProduct: (product: Product | null) => void;
  selectProductById: (productId: string) => void;

  // Actions - UI
  setLoading: (isLoading: boolean) => void;
  setFilters: (filters: Partial<ProductFilters>) => void;
  resetFilters: () => void;

  // Computed / Helpers
  getProductById: (id: string) => Product | undefined;
  getProductsByCategory: (category: string) => Product[];
  getActiveProducts: () => Product[];
  getFilteredProducts: () => Product[];
  searchProducts: (query: string) => Product[];
}

const defaultFilters: ProductFilters = {
  categoryId: undefined,
  zone: undefined,
  search: '',
  activeOnly: true,
};

export const useProductStore = create<ProductState>((set, get) => ({
  // Initial state
  products: [],
  isLoading: false,
  filters: defaultFilters,
  selectedProduct: null,

  // Actions - Products
  setProducts: (products) => set({ products }),

  addProduct: (product) =>
    set((state) => ({
      products: [...state.products, product],
    })),

  updateProduct: (productId, updates) =>
    set((state) => ({
      products: state.products.map((product) =>
        product.id === productId ? { ...product, ...updates } : product
      ),
      // Update selected product if it's the one being updated
      selectedProduct:
        state.selectedProduct?.id === productId
          ? { ...state.selectedProduct, ...updates }
          : state.selectedProduct,
    })),

  removeProduct: (productId) =>
    set((state) => ({
      products: state.products.filter((product) => product.id !== productId),
      selectedProduct:
        state.selectedProduct?.id === productId ? null : state.selectedProduct,
    })),

  clearProducts: () => set({ products: [], selectedProduct: null }),

  // Actions - Selection
  setSelectedProduct: (product) => set({ selectedProduct: product }),

  selectProductById: (productId) => {
    const { products } = get();
    const product = products.find((p) => p.id === productId) || null;
    set({ selectedProduct: product });
  },

  // Actions - UI
  setLoading: (isLoading) => set({ isLoading }),

  setFilters: (filters) =>
    set((state) => ({
      filters: { ...state.filters, ...filters },
    })),

  resetFilters: () => set({ filters: defaultFilters }),

  // Computed / Helpers
  getProductById: (id) => {
    const { products } = get();
    return products.find((product) => product.id === id);
  },

  getProductsByCategory: (category) => {
    const { products } = get();
    return products.filter((product) => product.categorie === category);
  },

  getActiveProducts: () => {
    const { products } = get();
    return products.filter((product) => product.actif);
  },

  getFilteredProducts: () => {
    const { products, filters } = get();

    return products.filter((product) => {
      // Filter by active status
      if (filters.activeOnly && !product.actif) {
        return false;
      }

      // Filter by category
      if (filters.categoryId && product.categorie_id !== filters.categoryId) {
        return false;
      }

      // Filter by search query
      if (filters.search && filters.search.trim() !== '') {
        const searchLower = filters.search.toLowerCase();
        const matchesName = product.nom.toLowerCase().includes(searchLower);
        const matchesCategory = product.categorie
          ?.toLowerCase()
          .includes(searchLower);
        const matchesBarcode = product.code_barre
          ?.toLowerCase()
          .includes(searchLower);

        if (!matchesName && !matchesCategory && !matchesBarcode) {
          return false;
        }
      }

      return true;
    });
  },

  searchProducts: (query) => {
    const { products } = get();

    if (!query || query.trim() === '') {
      return products;
    }

    const searchLower = query.toLowerCase().trim();

    return products.filter((product) => {
      const matchesName = product.nom.toLowerCase().includes(searchLower);
      const matchesCategory = product.categorie
        ?.toLowerCase()
        .includes(searchLower);
      const matchesBarcode = product.code_barre
        ?.toLowerCase()
        .includes(searchLower);
      const matchesFournisseur =
        typeof product.fournisseur === 'string' &&
        product.fournisseur.toLowerCase().includes(searchLower);

      return (
        matchesName || matchesCategory || matchesBarcode || matchesFournisseur
      );
    });
  },
}));
